import { useState } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Card } from './ui/card';
import { toast } from 'sonner';
import {
  Facebook, Instagram, Twitter, Send, MessageCircle, Linkedin, Mail,
  Copy, ExternalLink, Loader2, Sparkles, Zap,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const KFN = 'Kari Future Now Creations™';

/**
 * SmartShare — One-tap post to FB, IG, X, Threads, LinkedIn, WhatsApp, Email.
 * Plus dispatch to saved Buffer/Publer/Webhook destinations.
 * Zero Facebook App / Secret needed.
 */
export const SmartShare = ({ text, url, destinations = [], onReloadDestinations }) => {
  const [open, setOpen] = useState(false);
  const [links, setLinks] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(null);

  const openShare = async () => {
    setOpen(true);
    if (links) return;
    setLoading(true);
    try {
      const { data } = await axios.post(
        `${BACKEND_URL}/api/social/smart-share`,
        { text, url: url || window.location.origin },
        { withCredentials: true }
      );
      setLinks(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to build share links');
    } finally { setLoading(false); }
  };

  const copyAndOpen = (deeplink, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`Caption copied — opening ${label}...`);
    window.open(deeplink, '_blank', 'noopener');
  };

  const dispatchTo = async (dest) => {
    setSending(dest.id);
    try {
      const { data } = await axios.post(
        `${BACKEND_URL}/api/social/destinations/${dest.id}/send`,
        { text },
        { withCredentials: true }
      );
      if (data?.success) toast.success(`Sent to ${dest.name} ✅`);
      else toast.error(`${dest.name} replied ${data?.status_code || 'error'}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Send failed');
    } finally { setSending(null); }
  };

  const oneTapPlatforms = links
    ? [
        { id: 'facebook', label: 'Facebook', icon: Facebook, color: 'from-blue-600 to-blue-700', deeplink: links.facebook, mode: 'open' },
        { id: 'instagram', label: 'Instagram', icon: Instagram, color: 'from-pink-500 via-red-500 to-yellow-500', deeplink: links.instagram_web, mode: 'copy' },
        { id: 'twitter', label: 'X / Twitter', icon: Twitter, color: 'from-zinc-700 to-black', deeplink: links.twitter, mode: 'open' },
        { id: 'threads', label: 'Threads', icon: Send, color: 'from-zinc-800 to-zinc-900', deeplink: links.threads, mode: 'open' },
        { id: 'linkedin', label: 'LinkedIn', icon: Linkedin, color: 'from-blue-700 to-sky-700', deeplink: links.linkedin, mode: 'open' },
        { id: 'whatsapp', label: 'WhatsApp', icon: MessageCircle, color: 'from-emerald-500 to-green-600', deeplink: links.whatsapp, mode: 'open' },
        { id: 'email', label: 'Email', icon: Mail, color: 'from-orange-500 to-red-600', deeplink: links.email, mode: 'open' },
      ]
    : [];

  return (
    <>
      <Button
        onClick={openShare}
        data-testid="smart-share-trigger"
        className="bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 text-white font-bold shadow-lg shadow-orange-500/30"
      >
        <Zap className="w-4 h-4 mr-2" /> Smart Share
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl bg-[#0f0f17] border border-white/10 text-white" data-testid="smart-share-dialog">
          <DialogHeader>
            <DialogTitle className="text-2xl font-black flex items-center gap-2">
              <Zap className="w-6 h-6 text-orange-400" /> Smart Share
            </DialogTitle>
            <DialogDescription className="text-white/60">
              One-tap post to any platform. Zero FB app needed. Caption pre-filled. <span className="text-orange-300">{KFN}</span>
            </DialogDescription>
          </DialogHeader>

          {loading && <div className="flex items-center justify-center py-10"><Loader2 className="w-8 h-8 animate-spin text-orange-400" /></div>}

          {!loading && links && (
            <div className="space-y-5">
              {/* Caption preview */}
              <Card className="bg-white/5 border border-white/10 p-3">
                <div className="text-xs text-white/50 mb-1">Caption (will be pre-filled / copied)</div>
                <div className="text-sm text-white/90 whitespace-pre-wrap max-h-32 overflow-y-auto">{text}</div>
                <div className="mt-2 flex justify-end">
                  <Button size="sm" variant="ghost" onClick={() => { navigator.clipboard.writeText(text); toast.success('Copied'); }} className="text-orange-300 hover:bg-orange-500/10 h-7">
                    <Copy className="w-3 h-3 mr-1" /> Copy
                  </Button>
                </div>
              </Card>

              {/* One-tap platforms */}
              <div>
                <div className="text-xs uppercase tracking-wider text-white/50 mb-2">One-Tap Open</div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {oneTapPlatforms.map((p) => {
                    const Icon = p.icon;
                    return (
                      <Button
                        key={p.id}
                        data-testid={`smart-share-${p.id}`}
                        onClick={() => copyAndOpen(p.deeplink, p.label)}
                        className={`bg-gradient-to-br ${p.color} hover:brightness-110 text-white font-semibold h-auto py-3 flex flex-col items-center gap-1`}
                      >
                        <Icon className="w-5 h-5" />
                        <span className="text-xs">{p.label}</span>
                        {p.mode === 'copy' && <span className="text-[9px] opacity-80">copy + open</span>}
                      </Button>
                    );
                  })}
                </div>
                <p className="text-[11px] text-white/50 mt-2">
                  Facebook, X, Threads, LinkedIn open with caption <strong>pre-filled</strong>. Instagram copies the caption + opens IG (paste once).
                </p>
              </div>

              {/* Saved destinations */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="text-xs uppercase tracking-wider text-white/50">Auto-Send to Saved Destinations</div>
                  <Button size="sm" variant="ghost" onClick={onReloadDestinations} className="text-orange-300 hover:bg-orange-500/10 h-7 text-xs">Refresh</Button>
                </div>
                {destinations.length === 0 ? (
                  <div className="rounded-lg border border-dashed border-white/15 p-4 text-center text-sm text-white/50">
                    No destinations yet. Add a Buffer / Publer / Zapier webhook in the <strong>Destinations</strong> tab.
                  </div>
                ) : (
                  <div className="space-y-2">
                    {destinations.map((d) => (
                      <div key={d.id} className="flex items-center justify-between rounded-lg bg-white/5 border border-white/10 p-2.5">
                        <div className="flex items-center gap-3">
                          <div className="px-2 py-0.5 rounded-md bg-orange-500/15 border border-orange-500/30 text-orange-300 text-[10px] uppercase font-bold">{d.type}</div>
                          <div>
                            <div className="text-sm text-white font-semibold">{d.name}</div>
                            <div className="text-[11px] text-white/40">{d.target_preview}</div>
                          </div>
                        </div>
                        <Button
                          size="sm"
                          data-testid={`smart-share-send-${d.id}`}
                          onClick={() => dispatchTo(d)}
                          disabled={sending === d.id}
                          className="bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 text-white font-semibold h-8"
                        >
                          {sending === d.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Send className="w-3.5 h-3.5 mr-1" /> Send</>}
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex items-center justify-center gap-1.5 text-[10px] text-orange-300/80 pt-2 border-t border-white/5">
                <Sparkles className="w-3 h-3" /> {KFN}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

/**
 * Destinations Manager — add/list/delete Buffer / Publer / Webhook destinations.
 */
export const DestinationsManager = ({ destinations, onChange }) => {
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ name: '', type: 'webhook', target: '', platform: 'any', extra: '' });

  const save = async () => {
    if (!form.name || !form.target) { toast.error('Name and target required'); return; }
    setAdding(true);
    try {
      await axios.post(`${BACKEND_URL}/api/social/destinations`, form, { withCredentials: true });
      toast.success(`Added ${form.name}`);
      setForm({ name: '', type: 'webhook', target: '', platform: 'any', extra: '' });
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to add destination');
    } finally { setAdding(false); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this destination?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/social/destinations/${id}`, { withCredentials: true });
      toast.success('Deleted');
      onChange?.();
    } catch {
      toast.error('Delete failed');
    }
  };

  return (
    <div className="space-y-5">
      <Card className="bg-white/5 border border-white/10 p-5">
        <div className="text-lg font-bold text-white mb-1">Add a Destination</div>
        <div className="text-sm text-white/60 mb-4">
          Connect Buffer, Publer, Zapier, Make, or n8n — and post to FB/IG/X/LinkedIn <strong>without any Facebook App or secret key</strong>.
        </div>

        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-white/60 mb-1 block">Type</label>
            <select
              value={form.type}
              onChange={(e) => setForm({ ...form, type: e.target.value })}
              className="w-full bg-white/5 border border-white/10 text-white rounded-md p-2.5"
              data-testid="dest-type"
            >
              <option value="webhook">Zapier / Make / n8n Webhook</option>
              <option value="buffer">Buffer (Access Token)</option>
              <option value="publer">Publer (Access Token)</option>
            </select>
          </div>
          <div>
            <label className="text-xs text-white/60 mb-1 block">Friendly Name</label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="e.g. My FB Page (via Zapier)"
              data-testid="dest-name"
              className="w-full bg-white/5 border border-white/10 text-white rounded-md p-2.5"
            />
          </div>
          <div className="sm:col-span-2">
            <label className="text-xs text-white/60 mb-1 block">
              {form.type === 'webhook' ? 'Webhook URL (paste from Zapier / Make)' : 'Access Token'}
            </label>
            <input
              type="text"
              value={form.target}
              onChange={(e) => setForm({ ...form, target: e.target.value })}
              placeholder={form.type === 'webhook' ? 'https://hooks.zapier.com/...' : 'paste your token'}
              data-testid="dest-target"
              className="w-full bg-white/5 border border-white/10 text-white rounded-md p-2.5 font-mono text-xs"
            />
          </div>
          {form.type !== 'webhook' && (
            <div className="sm:col-span-2">
              <label className="text-xs text-white/60 mb-1 block">
                {form.type === 'buffer' ? 'Buffer Profile ID' : 'Publer Account ID(s) — comma separated'}
              </label>
              <input
                type="text"
                value={form.extra}
                onChange={(e) => setForm({ ...form, extra: e.target.value })}
                placeholder={form.type === 'buffer' ? 'e.g. 4f1f0c1a...' : 'acc_123, acc_456'}
                data-testid="dest-extra"
                className="w-full bg-white/5 border border-white/10 text-white rounded-md p-2.5 font-mono text-xs"
              />
            </div>
          )}
          <div>
            <label className="text-xs text-white/60 mb-1 block">Target Platform (label only)</label>
            <select
              value={form.platform}
              onChange={(e) => setForm({ ...form, platform: e.target.value })}
              className="w-full bg-white/5 border border-white/10 text-white rounded-md p-2.5"
            >
              <option value="any">Any</option>
              <option value="facebook">Facebook</option>
              <option value="instagram">Instagram</option>
              <option value="twitter">X / Twitter</option>
              <option value="linkedin">LinkedIn</option>
            </select>
          </div>
        </div>

        <Button
          onClick={save}
          disabled={adding}
          data-testid="dest-save-btn"
          className="mt-4 bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 text-white font-bold"
        >
          {adding ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
          Save Destination
        </Button>

        {/* Cheat sheets */}
        <details className="mt-4 text-xs text-white/60">
          <summary className="cursor-pointer text-orange-300 hover:text-orange-200">How do I get a webhook / token? (30-sec setup guides)</summary>
          <div className="mt-3 space-y-3">
            <div className="rounded-md bg-white/5 border border-white/10 p-3">
              <div className="font-bold text-white mb-1">Zapier (free, easiest)</div>
              <ol className="list-decimal list-inside space-y-0.5">
                <li>Go to <a className="text-orange-300 underline" href="https://zapier.com/app/zaps" target="_blank" rel="noreferrer">zapier.com/app/zaps</a> → Create Zap</li>
                <li>Trigger: <em>Webhooks by Zapier → Catch Hook</em></li>
                <li>Action: <em>Facebook Pages → Create Page Post</em> (Zapier handles the FB OAuth)</li>
                <li>Copy the webhook URL → paste it above. Done.</li>
              </ol>
            </div>
            <div className="rounded-md bg-white/5 border border-white/10 p-3">
              <div className="font-bold text-white mb-1">Buffer</div>
              <ol className="list-decimal list-inside space-y-0.5">
                <li>Sign up at <a className="text-orange-300 underline" href="https://buffer.com" target="_blank" rel="noreferrer">buffer.com</a> → connect your FB/IG once</li>
                <li>Go to <a className="text-orange-300 underline" href="https://buffer.com/developers/apps" target="_blank" rel="noreferrer">buffer.com/developers/apps</a> → Create an App → copy <em>Access Token</em></li>
                <li>Find your Profile ID from <code>buffer.com/developers/api/profiles</code></li>
                <li>Paste both above. Done.</li>
              </ol>
            </div>
            <div className="rounded-md bg-white/5 border border-white/10 p-3">
              <div className="font-bold text-white mb-1">Publer</div>
              <ol className="list-decimal list-inside space-y-0.5">
                <li>Sign up at <a className="text-orange-300 underline" href="https://publer.io" target="_blank" rel="noreferrer">publer.io</a> → connect FB/IG/X/LinkedIn once</li>
                <li>Settings → API → generate <em>Personal Access Token</em></li>
                <li>Find your Account ID(s) in the dashboard URL</li>
                <li>Paste both above. Done.</li>
              </ol>
            </div>
          </div>
        </details>
      </Card>

      <Card className="bg-white/5 border border-white/10 p-5">
        <div className="text-lg font-bold text-white mb-3">Your Destinations</div>
        {destinations.length === 0 ? (
          <div className="text-sm text-white/50">No destinations yet — add one above.</div>
        ) : (
          <div className="space-y-2">
            {destinations.map((d) => (
              <div key={d.id} className="flex items-center justify-between rounded-lg bg-white/5 border border-white/10 p-3">
                <div className="flex items-center gap-3">
                  <div className="px-2 py-0.5 rounded-md bg-orange-500/15 border border-orange-500/30 text-orange-300 text-[10px] uppercase font-bold">{d.type}</div>
                  <div>
                    <div className="text-sm text-white font-semibold">{d.name}</div>
                    <div className="text-[11px] text-white/40 font-mono">{d.target_preview}</div>
                  </div>
                </div>
                <Button size="sm" variant="ghost" onClick={() => remove(d.id)} className="text-red-400 hover:bg-red-500/10" data-testid={`dest-delete-${d.id}`}>
                  Delete
                </Button>
              </div>
            ))}
          </div>
        )}
      </Card>

      <div className="text-center text-[11px] text-orange-300/80 flex items-center justify-center gap-1.5">
        <ExternalLink className="w-3 h-3" /> Built with {KFN}
      </div>
    </div>
  );
};
